// Validasi field "Village Head Information" pada form Tambah Desa.
// Aturan direplikasi dari project mobile (Desa+) agar konsisten lintas aplikasi:
//   - name/username : 3-50 karakter, huruf/angka/spasi/.,'_- (fun_validateName)
//   - email         : format local@domain.tld (fun_validateEmail)
//   - phone         : 9-15 digit (pola form member/profile mobile)
//   - nik           : tepat 16 digit angka (standar NIK Indonesia)

export type VillageHead = {
  username: string
  nik: string
  email: string
  phone: string
  gender: string
}

const NAME_REGEX = /^[a-zA-Z0-9\s._,'-]{3,50}$/
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const NIK_REGEX = /^\d{16}$/

export function validateUsername(username: string): boolean {
  return NAME_REGEX.test(username.trim())
}

export function validateEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim())
}

/** NIK harus tepat 16 digit angka. */
export function validateNik(nik: string): boolean {
  return NIK_REGEX.test(nik.trim())
}

/** Nomor telepon 9-15 digit (mengikuti validasi member/profile di project mobile). */
export function validatePhone(phone: string): boolean {
  const trimmed = phone.trim()
  return trimmed.length >= 9 && trimmed.length <= 15
}

/**
 * Normalisasi nomor telepon ke format penyimpanan berawalan 62 — konsisten
 * dengan project mobile yang menyimpan `"62" + input`. User mengetik tanpa
 * awalan 62 (UI menampilkan prefix +62). Mengikuti mobile apa adanya:
 * tidak strip 0 di depan.
 */
export function normalizePhone(phone: string): string {
  return `62${phone.trim()}`
}

/**
 * Kebalikan normalizePhone untuk menampilkan nomor pada field ber-prefix +62.
 * Membuang awalan penyimpanan agar user melihat bagian lokalnya saja:
 *   - "62xxxx"  → "xxxx"  (format standar dari mobile/Add User)
 *   - "+62xxxx" → "xxxx"
 *   - "08xxxx"  → "8xxxx" (data lama; buang 0 di depan)
 * Nomor yang sudah tanpa prefix dikembalikan apa adanya.
 */
export function stripPhonePrefix(phone: string): string {
  const trimmed = phone.trim()
  if (trimmed.startsWith('+62')) return trimmed.slice(3)
  if (trimmed.startsWith('62')) return trimmed.slice(2)
  if (trimmed.startsWith('0')) return trimmed.slice(1)
  return trimmed
}

export const USERNAME_ERROR = "Village head name must be 3-50 characters (letters, numbers, spaces, . , ' _ -)."
export const NIK_ERROR = 'NIK must be exactly 16 digits.'
export const EMAIL_ERROR = 'Please enter a valid email address.'
export const PHONE_ERROR = 'Phone number must be 9-15 digits.'

/**
 * Pesan error inline per-field untuk ditampilkan saat user mengetik.
 * Mengembalikan null saat field masih KOSONG (jangan tandai merah sebelum diisi;
 * field wajib tetap dijaga saat submit) atau saat sudah valid.
 */
export function fieldError(field: 'username' | 'nik' | 'email' | 'phone', value: string): string | null {
  if (!value.trim()) return null
  switch (field) {
    case 'username':
      return validateUsername(value) ? null : USERNAME_ERROR
    case 'nik':
      return validateNik(value) ? null : NIK_ERROR
    case 'email':
      return validateEmail(value) ? null : EMAIL_ERROR
    case 'phone':
      return validatePhone(value) ? null : PHONE_ERROR
  }
}

/**
 * Validasi seluruh field village head. Mengembalikan pesan error pertama
 * yang ditemukan (untuk ditampilkan ke user), atau null jika semua valid.
 */
export function validateVillageHead(head: VillageHead): string | null {
  if (!validateUsername(head.username)) {
    return USERNAME_ERROR
  }
  if (!validateNik(head.nik)) {
    return NIK_ERROR
  }
  if (!validateEmail(head.email)) {
    return EMAIL_ERROR
  }
  if (!validatePhone(head.phone)) {
    return PHONE_ERROR
  }
  if (!head.gender) {
    return 'Please select a gender.'
  }
  return null
}
